
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PhotoIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import ApiCustom from "../../utils/ApiCustom";
import Medias from "./Medias";
import { fetchMedias } from "../../features/admin/MediaSlice";

const isBannerSelected = (media) =>
  media?.isBanner === true ||
  media?.isBanner === 1 ||
  media?.isBanner === "1";

const isImageMedia = (media) =>
  media?.imageableType === "product" && media?.imageType !== "video";

const SystemSettings = () => {
  const dispatch = useDispatch();
  const { medias, status, error } = useSelector((state) => state.medias);
  const [isBannerOpen, setIsBannerOpen] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    dispatch(fetchMedias());
  }, [dispatch]);

  const bannerImages = useMemo(
    () =>
      (Array.isArray(medias) ? medias : [])
        .filter(isImageMedia)
        .filter((media) => isBannerSelected(media)),
    [medias]
  );

  const handleRemoveBanner = async (media) => {
    if (!media?.id || removingId !== null || isSaving) {
      return;
    }

    setRemovingId(media.id);

    try {
      await ApiCustom.put(`/change-banner/${media.id}`, {
        isBanner: false,
      });

      toast.success("Đã xoá khỏi banner trang chủ");
      await dispatch(fetchMedias());
    } catch (err) {
      toast.error(
        err?.response?.data?.messages ||
          err?.response?.data?.message ||
          "Không thể xoá banner"
      );
    } finally {
      setRemovingId(null);
    }
  };

  const handleOpenMediaPicker = () => {
    setSelectedIds([]);
    setIsMediaPickerOpen(true);
  };

  const handleToggleSelect = (id) => {
    if (isSaving) {
      return;
    }

    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleAddSelectedBanners = async () => {
    if (selectedIds.length === 0 || isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      await Promise.all(
        selectedIds.map((id) =>
          ApiCustom.put(`/change-banner/${id}`, {
            isBanner: true,
          })
        )
      );

      toast.success("Đã thêm banner trang chủ");
      setSelectedIds([]);
      setIsMediaPickerOpen(false);
      await dispatch(fetchMedias());
    } catch (err) {
      toast.error(
        err?.response?.data?.messages ||
          err?.response?.data?.message ||
          "Không thể thêm banner"
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Cài đặt chung</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        <button
          type="button"
          onClick={() => setIsBannerOpen(true)}
          className="group bg-base-100 border border-base-300 rounded-2xl shadow-sm hover:shadow-md hover:border-blue-300 transition-all text-left p-5"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
              <PhotoIcon className="w-6 h-6 text-blue-600" />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold">Banner trang chủ</h2>
                <span className="badge badge-info badge-outline">
                  {bannerImages.length}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Quản lý hình ảnh hiển thị ở banner trang chủ.
              </p>
            </div>
          </div>
        </button>
      </div>

      {isBannerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9998] p-4">
          <div className="bg-base-100 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-5 border-b flex items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold">Banner trang chủ</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Các hình ảnh đang hiển thị trên trang chủ.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsBannerOpen(false)}
                className="btn btn-sm btn-ghost"
                disabled={removingId !== null || isSaving}
              >
                ✕
              </button>
            </div>

            <div className="px-6 py-4 border-b flex items-center justify-between gap-4">
              <div className="text-sm text-gray-500">
                {bannerImages.length} banner đang hiển thị
              </div>

              <button
                type="button"
                onClick={handleOpenMediaPicker}
                className="btn btn-success btn-sm gap-1"
                disabled={removingId !== null || isSaving}
              >
                <PlusIcon className="w-4 h-4" />
                Thêm banner
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 bg-base-200/40">
              {status === "loading" && bannerImages.length === 0 ? (
                <div className="py-14 text-center">Đang tải banner...</div>
              ) : error ? (
                <div className="py-14 text-center text-red-500">
                  Không thể tải danh sách banner.
                </div>
              ) : bannerImages.length === 0 ? (
                <div className="py-14 text-center text-gray-500">
                  Chưa có banner trang chủ.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {bannerImages.map((media) => (
                    <div
                      key={media.id}
                      className="group relative bg-base-100 border border-base-300 rounded-xl overflow-hidden shadow-sm"
                    >
                      <div className="aspect-[16/9] bg-gray-50">
                        <img
                          src={media.url}
                          alt="Banner"
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveBanner(media)}
                        disabled={removingId !== null || isSaving}
                        title="Xoá khỏi banner"
                        className="absolute top-3 right-3 btn btn-sm btn-error btn-circle shadow-md"
                      >
                        {removingId === media.id ? (
                          <span className="loading loading-spinner loading-xs"></span>
                        ) : (
                          <TrashIcon className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t flex justify-end bg-base-100">
              <button
                type="button"
                onClick={() => setIsBannerOpen(false)}
                className="btn btn-sm"
                disabled={removingId !== null || isSaving}
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {isMediaPickerOpen && (
        <Medias
          isModal={true}
          pickerMode={true}
          selectedIds={selectedIds}
          excludedIds={bannerImages.map((media) => media.id)}
          onToggleSelect={handleToggleSelect}
          onConfirmSelection={handleAddSelectedBanners}
          onClose={() => {
            if (!isSaving) {
              setSelectedIds([]);
              setIsMediaPickerOpen(false);
            }
          }}
          isSavingSelection={isSaving}
        />
      )}
    </div>
  );
};

export default SystemSettings;


